import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, from, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { InboundNoticeResultVO } from '@app/api/stock-service/model/inboundNoticeResultVO';
import { PurchaseOrderService } from '@app/feature/center-business/procurement/purchase-order/services/purchase-order.service';
import { PurchaseOrderDetailItem } from '@app/feature/center-business/procurement/purchase-order/purchase-order-add/purchase-order-add.config';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { WarehouseWarrantInfoVO } from '@app/api/model/warehouseWarrantInfoVO';
import { WarehouseWarrantDetailItem } from '../../models/warehouse-warrant-detail-item';
import { BASE_PATH } from '@app/api/stock-service/variables';
import { InboundService } from '../../inbound.service';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDatepickerComponent } from '@shared/components/advanced-datepicker/advanced-datepicker.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  getInboundStatusSeverity,
  getInboundStatusStyle,
} from '../../inbound.util';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

type InboundNoticeWrapperData = Partial<WarehouseWarrantInfoVO> & {
  innerOrderNo?: string;
  warehouseName?: string;
  warehouseCode?: string;
  deliveryDate?: string;
  expectedArrivalDate?: string;
  remarks?: string;
  realNoticeCode?: string;
  trackingNumber?: string;
  carrierName?: string;
};

@Component({
  selector: 'app-inbound-notice-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    DataTableComponent,
    AdvancedDatepickerComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './inbound-notice-edit.component.html',
  styleUrl: './inbound-notice-edit.component.css',
})
export class InboundNoticeEditComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private fb = inject(FormBuilder);
  private purchaseOrderService = inject(PurchaseOrderService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private translateService = inject(TranslateService);
  private http = inject(HttpClient);
  private basePath = inject(BASE_PATH);
  private destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  saving = signal(false);
  private skipCanDeactivate = false;
  editMode = signal(true); // Always in edit mode for edit component

  // Record details
  record = signal<InboundNoticeResultVO | null>(null);
  recordId = signal<number | null>(null);
  recordCode = signal<string | null>(null);

  // Purchase Order data
  purchaseOrderItems = signal<PurchaseOrderDetailItem[]>([]);
  inboundDataForWrapper = signal<InboundNoticeWrapperData | null>(null);
  inboundDetailItemsForWrapper = signal<WarehouseWarrantDetailItem[]>([]);

  // Form
  noticeForm!: FormGroup;

  // Table columns for Item Details
  itemTableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_code',
      prop: 'skuCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'inbound.fields.spu_code',
      prop: 'spuCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.spu_name',
      prop: 'spuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'inbound.fields.quantity',
      prop: 'plannedQty',
      type: 'text',
      width: 120,
      align: 'right',
    },
    {
      label: 'inbound.fields.lot',
      prop: 'batchNumber',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.expiry',
      prop: 'expiryDate',
      type: 'text',
      width: 150,
      align: 'left',
    },
  ];

  // Format item details with formatted numbers (plannedQty is formatted as string)
  formattedItemDetails = signal<any[]>([]);

  ngOnInit(): void {
    this.initializeForm();
    this.loadRecordDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.noticeForm = this.fb.group({
      code: [''],
      realNoticeCode: [''],
      warehouseCode: ['', Validators.required],
      warehouseName: [''],
      deliveryDate: [null],
      status: [''],
      remarks: [''],
      creationDate: [''],
      trackingNumber: [''],
      carrierName: [''],
    });
  }

  private resolveWarehouseDisplayName(
    code: string,
    fallback?: string,
  ): string {
    const cached = this.warehouseOptionsService.getWarehouseByCode(code);
    if (cached?.name) {
      return cached.name;
    }
    return fallback || code || '';
  }

  private prefetchWarehouseCode(code: string) {
    if (!code || this.warehouseOptionsService.getWarehouseByCode(code)) {
      return of(undefined);
    }
    return this.warehouseOptionsService
      .getOptionsPage({ page: 1, size: 1, searchText: code })
      .pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  private loadRecordDetails(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const code = this.route.snapshot.queryParams['code'];

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.onBack();
      return;
    }

    const noticeId = parseInt(id);
    if (isNaN(noticeId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.onBack();
      return;
    }

    this.recordId.set(noticeId);
    if (code) {
      this.recordCode.set(code);
    }

    this.loading.set(true);

    // Load notice data from API
    const url = `${this.basePath}/v1/inbound-notice/${noticeId}`;
    this.http
      .get<InboundNoticeResultVO>(url, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
        observe: 'body',
        responseType: 'json',
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notice: InboundNoticeResultVO) => {
          if (notice) {
            this.record.set(notice);
            if (notice.code) {
              this.recordCode.set(notice.code);
            }
            // Map details from notice.details to purchaseOrderItems
            if (notice.details && notice.details.length > 0) {
              const items: PurchaseOrderDetailItem[] = notice.details.map(
                (detail) => ({
                  id: detail.id?.toString() || '',
                  skuCode: detail.skuCode || '',
                  skuName: detail.skuName || '',
                  spuCode: detail.spuCode || '',
                  spuName: detail.spuName || '',
                  quantity: detail.expectedQty || 0,
                  warehouseCode: notice.warehouseCode || '',
                  unit: '',
                  unitPrice: detail.unitPrice || 0,
                  tax: 0,
                  totalValue:
                    (detail.expectedQty || 0) * (detail.unitPrice || 0),
                }),
              );
              this.purchaseOrderItems.set(items);
            }

            // Populate form with mapped warehouse name
            this.populateForm(notice);
            // Update wrapper data with mapped warehouse name
            this.updateWrapperData(notice);
            this.loading.set(false);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'inbound.notice.messages.not_found',
              ),
            });
            this.loading.set(false);
            this.onBack();
          }
        },
        error: (error: any) => {
          console.error('Error loading notice:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.notice.messages.failed_to_load',
            ),
          });
          this.onBack();
        },
      });
  }

  private populateForm(notice: InboundNoticeResultVO): void {
    if (!this.noticeForm) {
      return;
    }

    const warehouseCode = notice.warehouseCode || '';
    this.prefetchWarehouseCode(warehouseCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const warehouseName = this.resolveWarehouseDisplayName(
          warehouseCode,
          notice.warehouseName,
        );

        let deliveryDate: Date | null = null;
        if (notice.expectedArrivalDate) {
          deliveryDate = new Date(notice.expectedArrivalDate);
        }

        this.noticeForm.patchValue({
          code: notice.code || '',
          realNoticeCode: notice.realNoticeCode || '',
          warehouseCode: warehouseCode,
          warehouseName: warehouseName,
          deliveryDate: deliveryDate,
          status: notice.status?.toString() || '',
          remarks: notice.remarks || '',
          creationDate: notice.creationDate || '',
          trackingNumber: notice.trackingNumber || '',
          carrierName: notice.carrierName || '',
        });
      });
  }

  onBack(): void {
    this.router.navigate(['/stock/inbound'], {
      queryParams: { tab: 'inbound-notice' },
    });
  }

  onCancel(): void {
    this.onBack();
  }

  onSave(): void {
    // Get latest values from noticeForm (which should be synced from editForm)
    if (this.noticeForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'common.messages.required_fields_missing',
        ),
      });
      return;
    }

    this.saving.set(true);
    const formValue = this.noticeForm.getRawValue();
    const record = this.record();

    if (!record) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'inbound.notice.messages.not_found',
        ),
      });
      this.saving.set(false);
      return;
    }

    // Format delivery date
    let expectedArrivalDate: string | undefined = undefined;
    if (formValue.deliveryDate) {
      const date =
        formValue.deliveryDate instanceof Date
          ? formValue.deliveryDate
          : new Date(formValue.deliveryDate);
      expectedArrivalDate = date.toISOString();
    }

    const payload = {
      id: record.id,
      code: record.code || '', // code is readonly, không được edit
      realNoticeCode: formValue.realNoticeCode || record.realNoticeCode || '', // realNoticeCode is editable
      expectedArrivalDate: expectedArrivalDate || record.expectedArrivalDate,
      actualArrivalDate: record.actualArrivalDate,
      carrierName: formValue.carrierName || record.carrierName || '',
      trackingNumber: formValue.trackingNumber || record.trackingNumber || '',
      status: record.status,
      warehouseWarrantId: record.warehouseWarrantId,
      warehouseId: record.warehouseId,
      warehouseCode: record.warehouseCode,
      warehouseName: record.warehouseName,
      remarks: formValue.remarks || '',
    };

    const url = `${this.basePath}/v1/inbound-notice`;
    this.http
      .put(url, payload, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
        responseType: 'json',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.noticeForm?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.success'),
            detail: this.translateService.instant('common.messages.updated'),
          });
          this.onBack();
        },
        error: () => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'common.messages.failed_to_update',
            ),
          });
        },
      });
  }

  private updateWrapperData(notice: InboundNoticeResultVO): void {
    const warehouseName = this.resolveWarehouseDisplayName(
      notice.warehouseCode || '',
      notice.warehouseName,
    );

    this.inboundDataForWrapper.set({
      id: notice.id ?? undefined,
      code: notice.code,
      realNoticeCode: notice.realNoticeCode,
      innerOrderNo: notice.sourceCode, // sourceCode is the PO code
      warehouseCode: notice.warehouseCode,
      warehouseName: warehouseName,
      expectedArrivalDate: notice.expectedArrivalDate,
      status: notice.status?.toString(),
      remarks: notice.remarks || '',
      creationDate: notice.creationDate,
      trackingNumber: notice.trackingNumber || '',
      carrierName: notice.carrierName || '',
    });

    const mappedDetails: WarehouseWarrantDetailItem[] =
      this.purchaseOrderItems().map((item) => ({
        id: item.id ? Number(item.id) : undefined,
        skuCode: item.skuCode,
        skuName: item.skuName,
        spuCode: item.spuCode,
        spuName: item.spuName,
        plannedQty: item.quantity,
        alreadyQty: item.quantity,
        policy: {},
      }));

    this.inboundDetailItemsForWrapper.set(mappedDetails);
    this.updateFormattedItemDetails();
  }

  private updateFormattedItemDetails(): void {
    const items = this.inboundDetailItemsForWrapper().map((item) => ({
      ...item,
      plannedQty: this.formatNumber(item.plannedQty),
    }));
    this.formattedItemDetails.set(items);
  }

  private formatNumber(value: any): string {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'undefined' ||
      value === 'null'
    ) {
      return ' ';
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return ' ';
    }
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  // Get field value from inboundDataForWrapper
  getFieldValue(fieldName: keyof InboundNoticeWrapperData): string | undefined {
    const data = this.inboundDataForWrapper();
    if (!data) return undefined;
    return data[fieldName] as string | undefined;
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const time = date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const datePart = date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      return `${time} ${datePart}`;
    } catch {
      return dateString;
    }
  }

  getStatusLabel(status: number | string | undefined): string {
    if (!status) return '';

    // Handle string status (PENDING, RECEIVING, RECEIVED, CANCELLED)
    if (typeof status === 'string') {
      const statusMap: { [key: string]: string } = {
        PENDING: this.translateService.instant('inbound.status.pending'),
        RECEIVING: this.translateService.instant('inbound.status.receiving'),
        RECEIVED: this.translateService.instant('inbound.status.received'),
        CANCELLED: this.translateService.instant('inbound.status.cancelled'),
        CREATED: this.translateService.instant('inbound.status.created'),
        CONFIRMED: this.translateService.instant('inbound.status.confirmed'),
        IN_TRANSIT: this.translateService.instant('inbound.status.in_transit'),
        ARRIVED: this.translateService.instant('inbound.status.arrived'),
        PARTIALLY_RECEIVED: this.translateService.instant(
          'inbound.status.partially_received',
        ),
        CLOSED: this.translateService.instant('inbound.status.closed'),
      };
      if (statusMap[status]) {
        return statusMap[status];
      }
    }

    // Handle numeric status
    const statusNum = typeof status === 'string' ? parseInt(status) : status;
    const statusMap: { [key: number]: string } = {
      1: this.translateService.instant('inbound.status.created'),
      2: this.translateService.instant('inbound.status.confirmed'),
      3: this.translateService.instant('inbound.status.in_transit'),
      4: this.translateService.instant('inbound.status.arrived'),
      5: this.translateService.instant('inbound.status.receiving'),
      6: this.translateService.instant('inbound.status.partially_received'),
      7: this.translateService.instant('inbound.status.received'),
      8: this.translateService.instant('inbound.status.closed'),
      9: this.translateService.instant('inbound.status.cancelled'),
    };
    return statusMap[statusNum] || status.toString();
  }

  getStatusSeverity(
    status: number | string | undefined,
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return getInboundStatusSeverity(status);
  }

  getStatusStyle(status: number | string | undefined): any {
    return getInboundStatusStyle(status);
  }

  private formatDeliveryDate(date: Date | null): string {
    if (!date) return '';
    try {
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'fieldChange' && event.data && this.noticeForm) {
      // Sync form values from wrapper to noticeForm
      if (event.data.realNoticeCode !== undefined) {
        this.noticeForm.patchValue({
          realNoticeCode: event.data.realNoticeCode,
        });
      }
      if (event.data.trackingNumber !== undefined) {
        this.noticeForm.patchValue({
          trackingNumber: event.data.trackingNumber,
        });
      }
      if (event.data.carrierName !== undefined) {
        this.noticeForm.patchValue({ carrierName: event.data.carrierName });
      }
      if (event.data.deliveryDate !== undefined) {
        this.noticeForm.patchValue({ deliveryDate: event.data.deliveryDate });
      }
      if (event.data.remarks !== undefined) {
        this.noticeForm.patchValue({ remarks: event.data.remarks });
      }
    }
    if (event?.action === 'go_back') {
      this.onBack();
    }
    if (event?.action === 'cancel') {
      this.onCancel();
    }
    if (event?.action === 'save') {
      // Sync editForm data to noticeForm before saving
      if (event.data && this.noticeForm) {
        if (event.data.realNoticeCode !== undefined) {
          this.noticeForm.patchValue({
            realNoticeCode: event.data.realNoticeCode,
          });
        }
        if (event.data.deliveryDate !== undefined) {
          this.noticeForm.patchValue({ deliveryDate: event.data.deliveryDate });
        }
        if (event.data.remarks !== undefined) {
          this.noticeForm.patchValue({ remarks: event.data.remarks });
        }
      }
      this.onSave();
    }
  }

  // Placeholder to align with wrapper outputs
  onTableAction(event?: any): void {
    // no-op for edit mode
  }

  onStatusChange(event?: any): void {
    // no-op for edit mode
  }

  getWarehouseOptions() {
    return [];
  }

  // Helper method to get FormControl with proper typing
  getFormControl(controlName: string): any {
    return this.noticeForm.get(controlName) as any;
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.saving() || this.loading()) {
      return true;
    }
    return !(this.noticeForm?.dirty ?? false);
  }
}
